/**
 * O que faz: Botões de ação rápida para alterar o status de um agendamento.
 * As ações exibidas dependem do status atual (interface inteligente).
 * Estilo: pills compactas suaves, usadas na faixa de ações do AgendamentoCard.
 * Onde usa: Dentro do card de agendamento (AgendamentoCard.tsx).
 * Conexões: Recebe o objeto `agendamento` (status atual) e a função
 * `onStatusChange`, chamada com o novo status desejado ao clicar.
 * Tabelas: Nenhuma (a mutação sobe via callback para a CalendarPage).
 * Autor: IA (Kilo)
 * Data: 22/07/2026
 */
import React from 'react';
import { AgendamentoStatus } from '@/constants/agendamentos';
import { type AgendamentoFromDatabase } from '@/hooks/useAgendamentosData';
import { Check, X, Ban, DollarSign, CalendarCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AgendamentoStatusActionsProps {
  agendamento: AgendamentoFromDatabase;
  onStatusChange: (newStatus: AgendamentoStatus) => void;
}

// Estilo base das pills: compactas para caber na faixa de ações do card
// sem competir visualmente com o conteúdo principal.
const PILL_BASE =
  'inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-medium transition-colors';

interface AcaoStatus {
  novoStatus: AgendamentoStatus;
  rotulo: string;
  icone: React.ReactNode;
  classes: string;
}

export const AgendamentoStatusActions = ({
  agendamento,
  onStatusChange,
}: AgendamentoStatusActionsProps) => {
  const { status } = agendamento;

  // Impede que o clique na ação "vaze" para o card pai (que abriria a edição).
  const handleActionClick = (e: React.MouseEvent, newStatus: AgendamentoStatus) => {
    e.stopPropagation();
    onStatusChange(newStatus);
  };

  // Mapa de ações por status: centraliza rótulo, ícone e cores de cada transição,
  // eliminando a repetição de JSX que existia em cada case.
  const acoesPorStatus: Partial<Record<AgendamentoStatus, AcaoStatus[]>> = {
    [AgendamentoStatus.AGENDADO]: [
      {
        novoStatus: AgendamentoStatus.CONFIRMADO,
        rotulo: 'Confirmar',
        icone: <Check size={13} />,
        classes: 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100',
      },
      {
        novoStatus: AgendamentoStatus.CANCELADO,
        rotulo: 'Cancelar',
        icone: <X size={13} />,
        classes: 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100',
      },
    ],
    [AgendamentoStatus.CONFIRMADO]: [
      {
        novoStatus: AgendamentoStatus.REALIZADO,
        rotulo: 'Realizar',
        icone: <CalendarCheck size={13} />,
        classes: 'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100',
      },
      {
        novoStatus: AgendamentoStatus.NAO_COMPARECEU,
        rotulo: 'Não veio',
        icone: <Ban size={13} />,
        classes: 'border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100',
      },
      {
        novoStatus: AgendamentoStatus.CANCELADO,
        rotulo: 'Cancelar',
        icone: <X size={13} />,
        classes: 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100',
      },
    ],
    [AgendamentoStatus.REALIZADO]: [
      {
        novoStatus: AgendamentoStatus.PAGO,
        rotulo: 'Marcar como pago',
        icone: <DollarSign size={13} />,
        classes: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
      },
    ],
  };

  const acoes = acoesPorStatus[status as AgendamentoStatus];

  // Status finais (pago, cancelado, não compareceu) não têm ação: não renderiza nada.
  if (!acoes) return null;

  return (
    <>
      {acoes.map(acao => (
        <button
          key={acao.novoStatus}
          type="button"
          onClick={e => handleActionClick(e, acao.novoStatus)}
          className={cn(PILL_BASE, acao.classes)}
        >
          {acao.icone}
          {acao.rotulo}
        </button>
      ))}
    </>
  );
};
